import { useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import type { BreedingInventorySnapshot } from "../breeding/liveBreedingInventory";
import {
  buildPairFormationSummary,
  destinationTankIsEmpty,
  eligiblePairFormationSources,
  type PairFormationSourceOption,
  validatePairFormationConfirmation,
} from "../breeding/pairFormationFromStock";
import { buildPairFormFromStockOperation } from "../operations/breedingInventoryOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";
import { speakPersian } from "./audioGuidance";

interface PairFormationFromStockPanelProps {
  farmId: string;
  userId: string;
  snapshot: BreedingInventorySnapshot;
  destinationTankId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
  onCompleted: () => void;
}

function sourceOptionLabel(source: PairFormationSourceOption): string {
  return source.tankCode;
}

function workerError(code?: string): string {
  const messages: Record<string, string> = {
    PAIR_DESTINATION_OCCUPIED: "تانک مقصد خالی نیست. ابتدا یک تانک خالی انتخاب کنید.",
    DESTINATION_TANK_OCCUPIED:
      "تانک مقصد در همین فاصله اشغال شده است. اطلاعات سالن را به‌روزرسانی کنید.",
    DESTINATION_TANK_INVALID: "تانک مقصد دیگر برای تشکیل جفت در دسترس نیست.",
    TARGET_TANK_INVALID: "تانک مقصد دیگر برای تشکیل جفت در دسترس نیست.",
    PAIR_SOURCE_REQUIRED: "گله مبدأ نر و ماده را انتخاب کنید.",
    PAIR_VARIETY_REQUIRED: "نژاد یا واریته ماهی نر و ماده را ثبت کنید.",
    PAIR_SOURCE_QUANTITY_INSUFFICIENT: "تعداد ماهی در گله انتخاب‌شده برای تشکیل این جفت کافی نیست.",
    SOURCE_STOCK_QUANTITY_INSUFFICIENT: "موجودی گله تغییر کرده و برای تشکیل این جفت کافی نیست.",
    PAIR_ONLINE_REQUIRED: "تشکیل جفت نیاز به اتصال آنلاین دارد تا موجودی گله دوباره بررسی شود.",
    SOURCE_STOCK_NOT_FOUND: "گله مبدأ پیدا نشد. اطلاعات سالن را به‌روزرسانی کنید.",
    SOURCE_STOCK_NOT_ACTIVE: "گله مبدأ دیگر فعال نیست. اطلاعات سالن را به‌روزرسانی کنید.",
    SOURCE_STOCK_QUANTITY_UNKNOWN: "تعداد این گله نامشخص است. ابتدا موجودی گله را مشخص کنید.",
    SOURCE_STOCK_OCCUPANCY_MISSING: "تانک فعلی گله مشخص نیست. ابتدا وضعیت گله را اصلاح کنید.",
    ENTITY_VERSION_CHANGED: "موجودی گله تغییر کرده است. صفحه را به‌روزرسانی و دوباره بررسی کنید.",
    PERMISSION_DENIED: "شما اجازه تشکیل جفت را ندارید.",
  };
  return code
    ? (messages[code] ?? "تشکیل جفت توسط سرور تأیید نشد. اطلاعات را به‌روزرسانی کنید.")
    : "تشکیل جفت انجام نشد.";
}

export function PairFormationFromStockPanel({
  farmId,
  userId,
  snapshot,
  destinationTankId,
  onSyncSummary,
  onCompleted,
}: PairFormationFromStockPanelProps) {
  const destinationTank = snapshot.tanks.find((tank) => tank.id === destinationTankId);
  const sources = useMemo(() => eligiblePairFormationSources(snapshot), [snapshot]);
  const [maleSourceId, setMaleSourceId] = useState(sources[0]?.stockGroupId ?? "");
  const [femaleSourceId, setFemaleSourceId] = useState(
    sources.find((source, index) => index > 0 || source.quantity >= 2)?.stockGroupId ?? "",
  );
  const [maleVariety, setMaleVariety] = useState("");
  const [femaleVariety, setFemaleVariety] = useState("");
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const destinationEmpty = destinationTank
    ? destinationTankIsEmpty(snapshot, destinationTank.id)
    : false;
  const maleSource = sources.find((source) => source.stockGroupId === maleSourceId);
  const femaleOptions = sources.filter(
    (source) => source.stockGroupId !== maleSourceId || source.quantity >= 2,
  );
  const femaleSource = femaleOptions.find((source) => source.stockGroupId === femaleSourceId);

  const quantities = useMemo(
    () => new Map(sources.map((source) => [source.stockGroupId, source.quantity] as const)),
    [sources],
  );

  useEffect(() => {
    if (!sources.some((source) => source.stockGroupId === maleSourceId)) {
      setMaleSourceId(sources[0]?.stockGroupId ?? "");
    }
  }, [maleSourceId, sources]);

  useEffect(() => {
    if (!femaleOptions.some((source) => source.stockGroupId === femaleSourceId)) {
      setFemaleSourceId(femaleOptions[0]?.stockGroupId ?? "");
    }
  }, [femaleOptions, femaleSourceId]);

  function readGuide() {
    speakPersian(
      "برای تشکیل جفت، ابتدا گله مبدأ ماهی نر و نژاد آن را ثبت کنید. سپس گله مبدأ ماهی ماده و نژاد آن را ثبت کنید. سیستم از موجودی هر گله یک ماهی کم می‌کند و جفت را در تانک انتخاب شده می‌سازد.",
    );
  }

  function announceSource(label: "نر" | "ماده", sourceId: string) {
    const source = sources.find((item) => item.stockGroupId === sourceId);
    if (!source) return;
    speakPersian(
      `برای ماهی ${label}، تانک ${source.tankCode} انتخاب شد. موجودی ${source.quantity.toLocaleString("fa-IR")} عدد.`,
    );
  }

  function requestConfirmation() {
    setError(undefined);
    setMessage(undefined);

    const validation = validatePairFormationConfirmation({
      online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
      destinationTankEmpty: destinationEmpty,
      maleStockGroupId: maleSourceId,
      femaleStockGroupId: femaleSourceId,
      maleVariety,
      femaleVariety,
      quantities,
    });

    if (!validation.ok) {
      const text = workerError(validation.code);
      setError(text);
      speakPersian(text);
      return;
    }

    if (!destinationTank || !maleSource || !femaleSource) {
      const text = workerError("PAIR_SOURCE_REQUIRED");
      setError(text);
      return;
    }

    const operation = buildPairFormFromStockOperation(
      {
        actorId: userId,
        farmId,
        deviceId: getOrCreateDeviceId(),
        performedAt: new Date().toISOString(),
      },
      {
        destinationTankId: destinationTank.id,
        maleSourceStockGroupId: maleSource.stockGroupId,
        maleSourceStockVersion: maleSource.stockVersion,
        maleVariety,
        femaleSourceStockGroupId: femaleSource.stockGroupId,
        femaleSourceStockVersion: femaleSource.stockVersion,
        femaleVariety,
      },
    );

    const summary = buildPairFormationSummary({
      destinationTankCode: destinationTank.tankCode,
      maleSource,
      maleVariety,
      femaleSource,
      femaleVariety,
    });

    setPending(operation);
    setConfirmationParts(summary.parts);
  }

  async function confirm() {
    if (!pending) return;

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const text = workerError("PAIR_ONLINE_REQUIRED");
      setPending(undefined);
      setError(text);
      speakPersian(text);
      return;
    }

    try {
      setBusy(true);
      setError(undefined);

      const result = await persistAndSyncOperation(supabase, pending, true);
      onSyncSummary?.(result.summary);
      setPending(undefined);

      if (result.operation.status === "synced") {
        const text = "جفت جدید ثبت شد و موجودی گله‌ها به‌روزرسانی شد.";
        setMessage(text);
        speakPersian(text);
        onCompleted();
        return;
      }

      if (result.operation.status === "terminal_conflict") {
        setError(workerError(result.operation.lastErrorCode));
        return;
      }

      setError(
        "تأیید نهایی از سرور دریافت نشد. تشکیل جفت موفق اعلام نمی‌شود؛ اتصال را بررسی و اطلاعات را به‌روزرسانی کنید.",
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت تشکیل جفت انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (!destinationTank) {
    return <article className="manager-card settings-warning">تانک مقصد پیدا نشد.</article>;
  }

  if (!destinationEmpty) {
    return (
      <article className="manager-card">
        <h3>تانک {destinationTank.tankCode}</h3>
        <p className="settings-warning">
          این تانک خالی نیست. برای تشکیل جفت باید تانکی بدون جفت، مولد یا گله انتخاب شود.
        </p>
      </article>
    );
  }

  return (
    <article className="manager-card">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">تشکیل جفت</p>
          <h3>تانک مقصد: {destinationTank.tankCode}</h3>
        </div>
        <span className="muted-chip">✅ تانک خالی</span>
      </div>

      <button type="button" className="secondary-button large-button" onClick={readGuide}>
        🔊 راهنمای تشکیل جفت
      </button>

      {sources.length === 0 ? (
        <p className="settings-warning">
          گله فعالی با تعداد مشخص و تانک مبدأ معتبر برای انتخاب مولد وجود ندارد.
        </p>
      ) : (
        <div className="operation-field-grid">
          <div className="manager-card">
            <h3>♂️ ماهی نر</h3>
            <label>
              گله مبدأ نر
              <select
                value={maleSourceId}
                onChange={(event) => {
                  const value = event.target.value;
                  setMaleSourceId(value);
                  announceSource("نر", value);
                }}
              >
                {sources.map((source) => (
                  <option value={source.stockGroupId} key={source.stockGroupId}>
                    {sourceOptionLabel(source)}
                  </option>
                ))}
              </select>
            </label>
            {maleSource ? (
              <p className="section-note">
                تانک مبدأ: {maleSource.tankCode} · موجودی:{" "}
                {maleSource.quantity.toLocaleString("fa-IR")} عدد
              </p>
            ) : null}
            <label>
              نژاد / واریته نر
              <input
                value={maleVariety}
                onChange={(event) => setMaleVariety(event.target.value)}
                placeholder="مثلاً Red Melon"
              />
            </label>
          </div>

          <div className="manager-card">
            <h3>♀️ ماهی ماده</h3>
            <label>
              گله مبدأ ماده
              <select
                value={femaleSourceId}
                onChange={(event) => {
                  const value = event.target.value;
                  setFemaleSourceId(value);
                  announceSource("ماده", value);
                }}
              >
                {femaleOptions.map((source) => (
                  <option value={source.stockGroupId} key={source.stockGroupId}>
                    {sourceOptionLabel(source)}
                  </option>
                ))}
              </select>
            </label>
            {femaleSource ? (
              <p className="section-note">
                تانک مبدأ: {femaleSource.tankCode} · موجودی:{" "}
                {femaleSource.quantity.toLocaleString("fa-IR")} عدد
              </p>
            ) : null}
            <label>
              نژاد / واریته ماده
              <input
                value={femaleVariety}
                onChange={(event) => setFemaleVariety(event.target.value)}
                placeholder="مثلاً Pigeon Blood"
              />
            </label>
          </div>
        </div>
      )}

      <p className="section-note">
        کد جفت و کد دو مولد به‌صورت خودکار ساخته می‌شود. موجودی فقط پس از تأیید سرور کم می‌شود.
      </p>

      <button
        type="button"
        className="primary-button large-button"
        disabled={busy || sources.length === 0}
        onClick={requestConfirmation}
      >
        {busy ? "در حال بررسی…" : "بررسی و تشکیل جفت"}
      </button>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید تشکیل جفت"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirm()}
        />
      ) : null}
    </article>
  );
}
